import { Dynamo } from '.';

export const deliveryTypes = ['email', 'push'] as const;
export type DeliveryType = (typeof deliveryTypes)[number];

export const medicalProfileNotificationTypes = [
    'medical-profile-message',
    'medical-profile-activity-comment',
    'medical-profile-note-comment',
] as const;
export type MedicalProfileNotificationType = (typeof medicalProfileNotificationTypes)[number];

export const notificationTypes = [...medicalProfileNotificationTypes] as const;
export type NotificationType = (typeof notificationTypes)[number];

export type MedicalProfileMessageNotificationMetadata = {
    notificationType: 'medical-profile-message';
    auditLogId: string;
    messageContent: string;
    dateTime: string;
    messagingUserId: string;
};

export type MedicalProfileActivityCommentNotificationMetadata = {
    notificationType: 'medical-profile-activity-comment';
    auditLogId: string;
    auditLogCommentId: string;
    activityTitle: string;
    commentContent: string;
    dateTime: string;
    commentingUserId: string;
};

export type MedicalProfileNoteCommentNotificationMetadata = {
    notificationType: 'medical-profile-note-comment';
    noteId: string;
    noteCommentId: string;
    noteTitle: string;
    commentContent: string;
    noteEntityType: Dynamo.NoteEntityType;
    profileId: string;
    dateTime: string;
    commentingUserId: string;
};

export type NotificationMetadata =
    | MedicalProfileMessageNotificationMetadata
    | MedicalProfileActivityCommentNotificationMetadata
    | MedicalProfileNoteCommentNotificationMetadata;

export type NotificationMetadataForType<TType extends NotificationType> = Extract<
    NotificationMetadata,
    { notificationType: TType }
>;

export type MedicalProfileNotificationEvent<TType extends MedicalProfileNotificationType> = {
    notificationCategory: 'medical-profile';
    /**
     * The id of the notification (not currently used)
     */
    notificationId: string;
    /**
     * The id of the user who performed the action that triggered the notification
     */
    actorUserId: string;
    /**
     * The medical profile id that the notification is related to
     */
    medicalProfileId: string;
    /**
     * Metadata specific to the notification type
     */
    metadata: NotificationMetadataForType<TType>;
};

// For now, we are only using medical profile notifications
export type NotificationEvent<TType extends NotificationType> = MedicalProfileNotificationEvent<TType>;

export type InAppNotificationUserProps = {
    firstName: string;
    lastName: string;
};

export type MedicalProfileMessageInAppNotificationWithoutContactDetails = {
    notificationType: 'medical-profile-message';
    auditLogId: string;
    auditLogTitle: string;
    dateTime: string;
    userId: string;
};

export type MedicalProfileMessageInAppNotification = MedicalProfileMessageInAppNotificationWithoutContactDetails &
    InAppNotificationUserProps;

export type MedicalProfileActivityCommentInAppNotificationWithoutContactDetails = {
    notificationType: 'medical-profile-activity-comment';
    auditLogId: string;
    auditLogCommentId: string;
    messageContent: string;
    commentContent: string;
    dateTime: string;
    userId: string;
};

export type MedicalProfileActivityCommentInAppNotification =
    MedicalProfileActivityCommentInAppNotificationWithoutContactDetails & InAppNotificationUserProps;

export type MedicalProfileNoteCommentInAppNotificationWithoutContactDetails = {
    notificationType: 'medical-profile-note-comment';
    noteId: string;
    noteCommentId: string;
    noteEntityType: Dynamo.NoteEntityType;
    noteTitle: string;
    profileId: string;
    dateTime: string;
    commentContent: string;
    userId: string;
};

export type MedicalProfileNoteCommentInAppNotification =
    MedicalProfileNoteCommentInAppNotificationWithoutContactDetails & InAppNotificationUserProps;

export type InAppNotificationWithoutUserContactDetails =
    | MedicalProfileActivityCommentInAppNotificationWithoutContactDetails
    | MedicalProfileMessageInAppNotificationWithoutContactDetails
    | MedicalProfileNoteCommentInAppNotificationWithoutContactDetails;

export type InAppNotification =
    | MedicalProfileActivityCommentInAppNotification
    | MedicalProfileMessageInAppNotification
    | MedicalProfileNoteCommentInAppNotification;

export type AsUnreadInAppNotification<TNotification extends InAppNotification> = TNotification & {
    unreadInAppNotificationId: string;
    read: false;
};

export type UnreadInAppMedicalProfileActivityCommentNotification =
    AsUnreadInAppNotification<MedicalProfileActivityCommentInAppNotification>;

export type UnreadInAppMedicalProfileNoteCommentNotification =
    AsUnreadInAppNotification<MedicalProfileNoteCommentInAppNotification>;

export type UnreadInAppMedicalProfileMessageNotification =
    AsUnreadInAppNotification<MedicalProfileMessageInAppNotification>;

export type UnreadInAppNotification =
    | UnreadInAppMedicalProfileMessageNotification
    | UnreadInAppMedicalProfileActivityCommentNotification
    | UnreadInAppMedicalProfileNoteCommentNotification;

export type AsReadInAppNotification<TNotification extends InAppNotification> = TNotification & {
    read: true;
};

export type ReadInAppMedicalProfileActivityCommentNotification =
    AsReadInAppNotification<MedicalProfileActivityCommentInAppNotification>;

export type ReadInAppMedicalProfileNoteCommentNotification =
    AsReadInAppNotification<MedicalProfileNoteCommentInAppNotification>;

export type ReadInAppMedicalProfileMessageNotification =
    AsReadInAppNotification<MedicalProfileMessageInAppNotification>;

export type ReadInAppNotification =
    | ReadInAppMedicalProfileMessageNotification
    | ReadInAppMedicalProfileActivityCommentNotification
    | ReadInAppMedicalProfileNoteCommentNotification;

// bust the cache
export type ReadOrUnreadInAppNotification = ReadInAppNotification | UnreadInAppNotification;
